import { QueryDocumentSnapshot } from "@angular/fire/firestore"
import { PageEvent } from "@angular/material/paginator"
import { Sort, SortDirection } from "@angular/material/sort"
import { ApiModel, TrColumnFilterMap, TrDateFilter, TrGlobalFilterResult, TrSelectFilter } from "../models/models"
import { hasMatchingColumnByCheckbox, hasMatchingColumnByDate } from "../utils/date.utils"
import { BehaviorSubject } from "rxjs"

export interface DaoGetDataOptions {
  sortDirection?: SortDirection
  sortField?: string
  limit?: number
  offset?: number
}

export interface TrPageEvent extends PageEvent {
  isLastPage?: boolean
}

export abstract class DaoBase<T extends ApiModel, GetDataItemsType = T> {
  public sortHasChanged: boolean
  public scrollHasChanged: boolean
  public filterHasChanged: boolean
  public hasError: boolean
  public errorMessage = ""
  public allDataRetrieved = true
  public filter = ""
  public globalFilter: TrGlobalFilterResult = {} as any
  public columnsToFilter: string[] = []
  public dataMap: { [id: string]: T } = {}
  public totalCount: number
  public totalFiltered: T[]
  public options: DaoGetDataOptions
  public page: TrPageEvent = {} as any
  public selectAllForPageOnly = false
  public filterDefaults: TrColumnFilterMap = {}

  public currentData = new BehaviorSubject<T[]>([])
  public totalData = new BehaviorSubject<T[]>([])
  public currentSnapshots = new BehaviorSubject<Array<QueryDocumentSnapshot<any>>>([])
  public done = new BehaviorSubject<boolean>(false)
  public loading = new BehaviorSubject<boolean>(false)

  protected unsubscribe: () => void
  public showRow: (row: T) => boolean

  public abstract async initialize(noLimit?: boolean): Promise<void>
  public abstract async refreshData(): Promise<void>
  public abstract async deleteItem(id: string, deleteItemOnServer?: () => Promise<void>): Promise<void>
  public async deleteItems(ids: string[], deleteItemOnServer?: () => Promise<void>): Promise<void> {
    await Promise.all([
      ...ids.map(async id => this.deleteItem(id, null)),
    ])

    if (deleteItemOnServer) {
      await deleteItemOnServer()
    }
  }
  public reset(): void { return }
  public async getDataItems(data?: Array<GetDataItemsType | T>): Promise<T[]> { return (data || []) as T[] }
  public convertValuesToString(dataItem: T, key: string): string { return dataItem[key] }
  public getErrorMessage(err: any): string { return "" }

  /**
   * Returns the total count based on the page number and page size.
   */
  public getTotalCount(): number {
    return this.allDataRetrieved
      ? this.totalData.value.length
      : ((this.page.pageIndex || 0) + 1) * this.page.pageSize
  }

  public filterData(data: T[], filter: string, columnsToFilterBy: string[]): T[] {
    // Filter first by search
    const filteredData = data.filter(item => {
      return this.filterPredicate(item, filter, columnsToFilterBy)
          && this.hasMatchingGlobalFilter(item)
    })

    // Filter next by column/global filters
    if (this.hasColumnFilters()) {
      return filteredData.filter((item, i) => {
        const activeColumns = Object
          .keys(this.filterDefaults)
          .filter(column => this.isActiveFilter(column))
        return activeColumns.filter(column => {
          return this.hasMatchingFilter(item, column)
        }).length === activeColumns.length
      })
    }

    return filteredData
  }

  public search(value: string): void {
    this.filter = value
    this.updateDataFromFilter()
  }

  public async changePage(page: PageEvent, pageLimit: number): Promise<void> {
    this.page = page
    this.page.pageSize = page.pageSize || pageLimit
    this.options.limit = page.pageSize
    await this.refreshData()
  }

  public async sort(sort: Sort, pageLimit: number): Promise<void> {
    this.reset()
    this.options.sortDirection = sort.direction
    this.options.sortField = sort.active
    this.options.limit = pageLimit
    this.page.pageIndex = 0
    this.page.previousPageIndex = 0
    if (this.allDataRetrieved) {
      this.refreshData()
    } else {
      await this.initialize()
    }
  }

  public hasColumnFilters(): boolean {
    return !!Object.keys(this.filterDefaults).find(column => column && this.isActiveFilter(column))
  }
  /**
   * Originally taken from: https://github.com/angular/material2/blob/master/src/lib/table/table-data-source.ts
   * Checks if a data object matches the data source's filter string. By default, each data object
   * is converted to a string of its properties and returns true if the filter has
   * at least one occurrence in that string. By default, the filter string has its whitespace
   * trimmed and the match is case-insensitive. May be overridden for a custom implementation of
   * filter matching.
   * @param data Data object used to check against the filter.
   * @param filter Filter string that has been set on the data source.
   * @returns Whether the filter matches against the data
   */
  public filterPredicate(data: T, filter: string, columnsToFilterBy: string[]): boolean {
    // Transform the data into a lowercase string of all property values.
    const dataStr = columnsToFilterBy
      .reduce((currentTerm: string, key: string) => {
        // Use an obscure Unicode character to delimit the words in the concatenated string.
        // This avoids matches where the values of two columns combined will match the user's query
        // (e.g. `Flute` and `Stop` will match `Test`). The character is intended to be something
        // that has a very low chance of being typed in by somebody in a text field. This one in
        // particular is "White up-pointing triangle with dot" from
        // https://en.wikipedia.org/wiki/List_of_Unicode_characters
        return `${currentTerm}${this.convertValuesToString(data, key)}◬`
      }, "").toLowerCase()

    // Transform the filter by converting it to lowercase and removing whitespace.
    const transformedFilter = `${filter}`.trim().toLowerCase()

    return dataStr.indexOf(transformedFilter) !== -1
  }

  public isActiveFilter(column: string): boolean {
    const filter = this.filterDefaults[column]
    if (!filter) { return false }
    const filterDefined = this.filterDefaults && !!filter.type
    if (!filterDefined) { return false }

    if (filter.type === "checkbox") {
      return filter.data !== undefined
    } else if (filter.type === "dropdown") {
      if ((filter.data as TrSelectFilter).singleValue) {
        return !!(filter.data as TrSelectFilter).singleValue
      } else {
        return (filter.data as TrSelectFilter).selections.length > 0
      }
    } else if (filter.type === "date") {
      return Object.keys(filter.data).length > 0
    }

    return filterDefined
  }

  public refreshWithCache(data: T[], filteredData: (data: T[]) => T[]): void {
    this.allDataRetrieved = true
    const dataFiltered = filteredData(data)
    this.totalData.next(dataFiltered)
    this.updateCachedData(dataFiltered)
  }

  public async updateItem(value: T, updateItemOnServer?: (value: T) => Promise<any>): Promise<void> {
    this.dataMap[value._id] = value
    if (updateItemOnServer) {
      this.loading.next(true)
      await updateItemOnServer(value)
      this.loading.next(false)
    }
    const newTotalData = this.totalData.value.map(item => value._id === item._id ? value : item)
    this.updateCachedData(newTotalData)
  }

  public async addItem(value: T, addItemOnServer?: (value: T) => Promise<any>): Promise<void> {
    await this.onAddItem(value, addItemOnServer)
    const newTotalData = this.totalData.value
    newTotalData.push(value)
    this.updateCachedData(newTotalData)
  }

  public async onAddItem(value: T, addItemOnServer?: (value: T) => Promise<string | void>): Promise<void> {
    this.dataMap[value._id] = value
    if (addItemOnServer) {
      this.loading.next(true)
      const id = await addItemOnServer(value)
      if (id) {
        this.dataMap[value._id]._id = id
        value._id = id
      }
      this.loading.next(false)
    }
  }

  public updateCachedData(values: T[]): void {
    if (this.options.sortField) {
      values.sort((firstItem, nextItem) => {
        const isAsc = this.options.sortDirection === "asc" || this.options.sortDirection === undefined
        return this.compareValues(
          this.convertValuesToString(firstItem, this.options.sortField),
          this.convertValuesToString(nextItem, this.options.sortField),
          isAsc,
        )
      })
    }

    this.totalFiltered = this.filterData(values, this.filter, this.columnsToFilter)
    this.totalCount = this.totalFiltered.length
    this.page.length = this.totalCount

    let startIndex = 0
    let endIndex = this.totalFiltered.length
    if (this.page) {
      startIndex = (this.page.pageIndex || 0) * this.page.pageSize
      endIndex = ((this.page.pageIndex || 0) + 1) * this.page.pageSize
    }

    const filteredByPage = this.totalFiltered.slice(startIndex, endIndex)

    this.currentData.next(filteredByPage)
  }

  public async updateDataFromFilter(): Promise<void> {
    this.page.pageIndex = 0
    this.page.previousPageIndex = 0
    if (!this.allDataRetrieved) {
      await this.initialize()
    } else {
      this.refreshData()
    }
  }

  public hasMatchingGlobalFilter(item: T): boolean {
    // Implement this.globalFilter...
    return true
  }

  public hasMatchingFilter(item: T, column: string): boolean {
    const columnStrValue = this.convertValuesToString(item, column)

    // Allow for deep column ids like customFields.someValue.name
    let columnValue
    const columnKeys = column.split(".")
    if (columnKeys.length > 1) {
      // TODO: Should this be here? For custom fields?
      if (columnKeys[0] === "customFields") {
        columnKeys.push("value")
      }
      columnValue = item[columnKeys[0]]
      columnKeys.slice(1).forEach(key => {
        if (key === "value" && columnKeys[0] === "customFields" && columnValue && columnValue[key] === undefined) {
          return
        }
        columnValue = columnValue ? columnValue[key] : columnValue
      })
    } else {
      columnValue = item[column]
    }

    const filter = this.filterDefaults[column]

    if (filter.type === "checkbox") {
      return this.hasMatchingColumnByCheckbox(columnStrValue, filter.data as boolean)
          || this.hasMatchingColumnByInput(item, filter.data as string, column)
    } else if (filter.type === "date") {
      return this.hasMatchingColumnByDate(columnValue, filter.data as TrDateFilter)
    } else if (filter.type === "dropdown") {
      return this.hasMatchingColumnByDropdown((!columnValue || typeof columnValue === "string") ? columnStrValue : columnValue, filter.data as TrSelectFilter)
    } else if (filter.type === "input") {
      return this.hasMatchingColumnByInput(item, filter.data as string, column)
    }

    return false
  }

  public hasMatchingColumnByInput(item: T, inputFilter: string, column: string): boolean {
    return this.filterPredicate(item, inputFilter, [ column ])
  }
  public hasMatchingColumnByDropdown(columnValue: string | string[], dropdownFilter: TrSelectFilter): boolean {
    if (dropdownFilter.singleValue) {
      return (columnValue || "").includes(dropdownFilter.singleValue)
    } else {
      const selectedAmt = dropdownFilter.selections
        .filter(value => (columnValue || "").includes(value))
        .length

      return dropdownFilter.isAll ? selectedAmt === dropdownFilter.selections.length : selectedAmt > 0
    }
  }

  public hasMatchingColumnByCheckbox(columnValue: string | boolean, checkboxValue: boolean): boolean {
    return hasMatchingColumnByCheckbox(columnValue, checkboxValue)
  }

  public hasMatchingColumnByDate(columnValue: Date, dateFilter: TrDateFilter): boolean {
    return hasMatchingColumnByDate(columnValue, dateFilter)
  }

  public compareValues(currentItem: string | number, nextItem: string | number, isAsc: boolean): number {
    return `${currentItem}`.localeCompare(`${nextItem}`, undefined, {numeric: true, sensitivity: "base"}) * (isAsc ? 1 : -1)
  }

  public getDataItemById(id: string): T {
    return this.dataMap[id]
  }

  public getPotentialSelections(): T[] {
    // TODO: Add global filter check when implemented
    if (this.filter || this.hasColumnFilters()) {
      return this.totalFiltered
    } else if (this.allDataRetrieved) {
      return this.selectAllForPageOnly ? this.currentData.value : this.totalData.value
    } else {
      // TODO: When we do paging and want to select all items for a search, we will need to select all items from a query...
      return this.currentData.value
    }
  }
}
